import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
} from '@kollektive/design-system';
import { AlertTriangle } from 'lucide-react';
import type { UserToken } from '@/types';
import styles from './RevokeTokenDialog.module.css';

interface RevokeTokenDialogProps {
  token: UserToken;
  tokenCount?: number;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

export function RevokeTokenDialog({
  token,
  tokenCount = 1,
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}: RevokeTokenDialogProps) {
  const isBulk = tokenCount > 1;
  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Modal open={isOpen} onClose={handleClose} size="sm">
      <ModalBody>
        <div className={styles.content}>
          {/* Warning icon */}
          <div className={styles.iconWrapper}>
            <AlertTriangle size={24} className={styles.errorIcon} />
          </div>

          <h2 className={styles.title}>
            {isBulk ? `Revoke ${tokenCount} Tokens` : 'Revoke Token'}
          </h2>

          <p className={styles.description}>
            {isBulk ? (
              <>
                Are you sure you want to revoke <strong>{tokenCount} tokens</strong>?
                This action cannot be undone and any applications using these tokens
                will immediately lose access.
              </>
            ) : (
              <>
                Are you sure you want to revoke this{' '}
                <strong>{token.role}</strong> token? This action cannot be undone
                and any applications using this token will immediately lose access.
              </>
            )}
          </p>

          {/* Token info - only show for single token */}
          {!isBulk && (
            <Card>
              <CardBody>
                <div className={styles.tokenInfo}>
                  <span className={styles.tokenLabel}>Role</span>
                  <span className={styles.tokenValue}>{token.role}</span>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={onConfirm}
          disabled={isLoading}
        >
          {isLoading
            ? 'Revoking...'
            : isBulk
              ? `Revoke ${tokenCount} Tokens`
              : 'Revoke Token'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
